import * as React from "react"
import { useEffect, useId, useRef, useState } from "react"
import { Box } from "@mui/material"

import { announcementColors } from "Theme/palette"

/**
 * Renders an announcement body inside a sandboxed iframe that grows to fit its content.
 *
 * The body is HTML written by a human and stored in the database, so it is treated as
 * untrusted. The sandbox carries `allow-scripts` but **NEVER `allow-same-origin`**, and the
 * two must not be combined: together they cancel the sandbox entirely and turn any
 * announcement into stored XSS against every user who opens the popup. On its own,
 * `allow-scripts` leaves the document in an opaque origin — it cannot read the app's
 * cookies, its `localStorage` or its DOM, and it cannot navigate the tab.
 *
 * What `allow-scripts` buys is the measurement below: the document reports its own height
 * through `postMessage` and the frame follows it. Without it the height had to be fixed, and
 * a two-line announcement left a modal three quarters empty.
 *
 * What it costs: the uploaded HTML can run its own JavaScript inside that opaque box — call
 * out to a third party, open a tab through `allow-popups`. The bodies are written by our own
 * staff through the News Management dashboard, not by customers, which is the reason the
 * trade is acceptable here. If that ever changes, this is the line to revisit.
 */

/**
 * Re-exported so the popup, the bell and the editor cannot drift apart into three different
 * blues. The values themselves live in `Theme/palette`, next to the rest of the brand.
 */
export const ANNOUNCEMENT_BRAND_SURFACE = announcementColors.brandSurface
export const ANNOUNCEMENT_CTA = announcementColors.cta
export const ANNOUNCEMENT_CTA_HOVER = announcementColors.ctaHover
export const ANNOUNCEMENT_SURFACE_SHADOW = announcementColors.surfaceShadow

/** The document announces its height; the parent listens. Namespaced because `postMessage`
 *  with `"*"` is a shared channel and other widgets post into the same window. */
const HEIGHT_MESSAGE = "ds-announcement-height"

/** Guardrails for whatever the document reports: a broken layout must not produce a
 *  ten-thousand-pixel dialog, and an empty one must still be clickable. */
const MIN_HEIGHT = 80
const MAX_HEIGHT = 4000

const heightReporter = frameId => `
    <script>
        (function () {
            var id = ${JSON.stringify(frameId)};
            var last = 0;

            function measure() {
                var body = document.body;
                if (!body) return 0;
                var styles = window.getComputedStyle(body);
                // Body margins sit outside scrollHeight, and a full uploaded document
                // usually brings the browser's default 8px.
                return Math.ceil(
                    body.scrollHeight
                    + parseFloat(styles.marginTop || 0)
                    + parseFloat(styles.marginBottom || 0)
                );
            }

            function send() {
                var height = measure();
                // A dead band, so a sub-pixel reflow cannot start a resize loop.
                if (!height || Math.abs(height - last) < 2) return;
                last = height;
                parent.postMessage({ type: ${JSON.stringify(HEIGHT_MESSAGE)}, id: id, height: height }, "*");
            }

            function start() {
                send();
                if (window.ResizeObserver) new ResizeObserver(send).observe(document.body);
            }

            if (document.body) start();
            else document.addEventListener("DOMContentLoaded", start);
            // Images land after DOMContentLoaded and change the height when they do.
            window.addEventListener("load", send);
        })();
    </script>
`

/**
 * The only thing standing between a hand-written announcement and how it looks, so it is
 * written as a small stylesheet rather than a handful of resets: whoever writes the body
 * pastes markup, not CSS, and everything they paste lands here.
 *
 * It is deliberately element-only — no classes. A class-based system would have to be
 * documented and remembered; plain `<h2>`, `<ul>` and `<table>` come out right on their own.
 */
const BASE_STYLES = `
    <style>
        :root { color-scheme: light; }
        * { box-sizing: border-box; }
        body {
            margin: 0 auto;
            padding: 24px;
            /* A comfortable measure, not the full width of the modal. */
            max-width: 640px;
            font-family: "Inter", "Helvetica", "Arial", sans-serif;
            font-size: 15px;
            line-height: 1.7;
            color: ${announcementColors.ink};
            -webkit-font-smoothing: antialiased;
        }
        /* The frame already provides the outer padding, so the first and last blocks must
           not add their own margin on top of it. */
        body > :first-child { margin-top: 0; }
        body > :last-child { margin-bottom: 0; }

        h1, h2, h3, h4 {
            margin: 1.6em 0 0.5em;
            line-height: 1.3;
            font-weight: 600;
            color: ${announcementColors.inkStrong};
        }
        h1 { font-size: 24px; }
        h2 { font-size: 19px; }
        h3 { font-size: 16px; }
        h4 { font-size: 15px; }
        p { margin: 0 0 1em; }
        small { font-size: 13px; color: ${announcementColors.inkMuted}; }

        a { color: ${announcementColors.link}; text-decoration: underline; text-underline-offset: 2px; }
        a:hover { color: ${announcementColors.linkHover}; }

        /* A link alone in its paragraph is a call to action, not a line of prose — the
           convention changelog tools have settled on. It costs the writer nothing: they
           already put the link on its own line. */
        p > a:only-child {
            display: inline-block;
            padding: 11px 22px;
            border-radius: 8px;
            background: ${announcementColors.cta};
            color: #fff;
            font-weight: 600;
            text-decoration: none;
        }
        p > a:only-child:hover { background: ${announcementColors.ctaHover}; color: #fff; }

        img { display: block; max-width: 100%; height: auto; border-radius: 6px; }
        figure { margin: 1.25em 0; }
        figcaption { margin-top: 0.5em; font-size: 13px; color: ${announcementColors.inkMuted}; }

        /* An opening image leads the announcement edge to edge, under the dialog header,
           the way changelog tools present a release. Nothing is asked of the writer beyond
           putting the image first. */
        body > img:first-child,
        body > figure:first-child > img,
        body > p:first-child:has(> img:only-child) > img {
            width: calc(100% + 48px);
            max-width: none;
            margin: -24px -24px 20px -24px;
            border-radius: 0;
        }
        body > figure:first-child,
        body > p:first-child:has(> img:only-child) { margin-top: 0; }

        mark { padding: 0.05em 0.25em; border-radius: 3px; background: #FEF3C7; color: #78350F; }

        /* Somewhere to put the long explanation without making everyone scroll past it. */
        details {
            margin: 0 0 1em;
            padding: 10px 14px;
            border: 1px solid ${announcementColors.border};
            border-radius: 8px;
            background: ${announcementColors.surfaceMuted};
        }
        details[open] { padding-bottom: 14px; }
        summary {
            cursor: pointer;
            font-weight: 600;
            color: ${announcementColors.inkStrong};
        }
        summary::marker { color: #9CA3AF; }

        kbd {
            padding: 0.1em 0.4em;
            border: 1px solid ${announcementColors.border};
            border-bottom-width: 2px;
            border-radius: 4px;
            background: #fff;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 0.85em;
            color: ${announcementColors.inkStrong};
        }

        ul, ol { margin: 0 0 1em; padding-left: 1.35em; }
        li { margin-bottom: 0.35em; }

        blockquote {
            margin: 1.25em 0;
            padding: 0.25em 0 0.25em 1em;
            border-left: 3px solid ${announcementColors.border};
            color: #4B5563;
        }
        hr { margin: 1.75em 0; border: 0; border-top: 1px solid ${announcementColors.border}; }

        code {
            padding: 0.15em 0.35em;
            border-radius: 4px;
            background: #F3F4F6;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 0.9em;
        }
        pre {
            margin: 0 0 1em;
            padding: 12px;
            border-radius: 6px;
            background: #F3F4F6;
            overflow-x: auto;
        }
        pre code { padding: 0; background: none; }

        table {
            width: 100%;
            margin: 0 0 1em;
            border-collapse: collapse;
            font-size: 13px;
        }
        th, td { padding: 8px 10px; border: 1px solid ${announcementColors.border}; text-align: left; }
        th { background: ${announcementColors.surfaceMuted}; font-weight: 600; color: ${announcementColors.inkStrong}; }
    </style>
`

function buildDocument(html, frameId) {
    if (!html) return ""

    const head = `${BASE_STYLES}${heightReporter(frameId)}`

    // A full document brings its own <head>, so it cannot be wrapped — but it still gets the
    // base stylesheet, injected FIRST so anything the document declares wins over it. Without
    // this, uploading a complete `.html` silently opted out of every style below.
    const isFullDocument = /<html[\s>]/i.test(html)
    if (isFullDocument) {
        if (/<head[\s>]/i.test(html)) {
            return html.replace(/<head([^>]*)>/i, `<head$1>${head}`)
        }
        return html.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`)
    }

    return `<!DOCTYPE html><html><head><meta charset="utf-8">${head}</head><body>${html}</body></html>`
}

/** `height` is the starting point and the fallback, in pixels or any CSS length. Once the
 *  document reports its own size the frame follows it, so callers only need to say how tall
 *  it should be before the first measurement arrives. Pass `fixedHeight` to opt out and keep
 *  a panel of a constant size — the editor's preview does, so the form does not jump around
 *  while someone types. */
export default function AnnouncementFrame({
    html,
    title = "Announcement",
    height = 360,
    bordered = false,
    fixedHeight = false,
    sx = {},
}) {
    // Each frame tags its own messages: several of these can be mounted at once (the popup
    // and the preview), and they all post into the same window.
    const frameId = useId()
    const frameRef = useRef(null)

    // The debounce keeps the frame from reloading on every keystroke while someone edits the
    // HTML by hand; the `version` counter keys the iframe so each new document remounts it
    // rather than relying on the browser to re-parse a swapped `srcDoc`.
    const [{ doc, version }, setDoc] = useState({ doc: buildDocument(html, frameId), version: 0 })
    const [measured, setMeasured] = useState(null)

    useEffect(() => {
        const timer = setTimeout(
            () => setDoc(previous => ({ doc: buildDocument(html, frameId), version: previous.version + 1 })),
            200,
        )
        return () => clearTimeout(timer)
    }, [html, frameId])

    useEffect(() => {
        if (fixedHeight) return undefined

        const onMessage = event => {
            // The sandbox has no `allow-same-origin`, so `event.origin` is the string "null"
            // for every frame and proves nothing. Identity of the sending window does:
            // only our own iframe can be this exact `contentWindow`.
            if (event.source !== frameRef.current?.contentWindow) return
            if (event.data?.type !== HEIGHT_MESSAGE || event.data?.id !== frameId) return

            const reported = Number(event.data.height)
            if (!Number.isFinite(reported)) return
            setMeasured(Math.min(Math.max(reported, MIN_HEIGHT), MAX_HEIGHT))
        }

        window.addEventListener("message", onMessage)
        return () => window.removeEventListener("message", onMessage)
    }, [frameId, fixedHeight])

    // A new document is a new size: keeping the old one would leave the frame stretched to
    // the previous announcement until the next measurement lands.
    useEffect(() => { setMeasured(null) }, [version])

    return (
        // Borderless by default. Inside a dialog the frame would otherwise draw a box within
        // a box, and any slack left over by the fixed height would read as an empty panel
        // instead of as plain white space. Only the editor's preview asks for the border,
        // where the point IS to show where the announcement ends.
        <Box
            sx={{
                ...(bordered && {
                    border: `1px solid ${announcementColors.border}`,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                }),
                overflow: 'hidden',
                ...sx,
            }}
        >
            <iframe
                key={version}
                ref={frameRef}
                title={title}
                srcDoc={doc}
                // `allow-scripts` WITHOUT `allow-same-origin`. Never both: see the note at
                // the top of this file before touching this line.
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                style={{
                    display: 'block',
                    width: '100%',
                    height: measured ?? height,
                    border: 0,
                    // Only while waiting for the first measurement, so the frame does not
                    // visibly snap from the fallback height to the real one.
                    transition: 'height .15s ease',
                }}
            />
        </Box>
    )
}
